// Plays-by-year strip (contract D.4.2): an inline SVG with one column per
// year over `years` ([from, to], widened to cover the data), in var(--accent)
// on a hairline baseline. Each year has a hover tooltip and a <title>; the
// numbers are also in a visually hidden table for screen readers.
//
// Props: { playsByYear: [{ year, n }], years: [from, to] }

import React, { useEffect, useRef, useState } from 'react';
import './vibe.css';

const PLOT_H = 56;
const TOP = 14; // room for the peak label
const BOTTOM = 18; // room for year labels
const MAX_YEARS = 150;

const colPath = (x, y, w, h) => {
  const r = Math.min(4, w / 2, h);
  const b = y + h;
  return (
    'M' + x + ',' + b + 'V' + (y + r) + 'Q' + x + ',' + y + ' ' + (x + r) + ',' + y +
    'H' + (x + w - r) + 'Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + r) + 'V' + b + 'Z'
  );
};

export default function YearStrip({ playsByYear, years }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(460);
  const [hover, setHover] = useState(null);


  const counts = {};
  (Array.isArray(playsByYear) ? playsByYear : []).forEach((r) => {
    const y = r && parseInt(r.year, 10);
    const n = r && Number(r.n);
    if (Number.isFinite(y) && Number.isFinite(n) && n > 0) counts[y] = (counts[y] || 0) + n;
  });
  const dataYears = Object.keys(counts).map(Number);
  let from = Array.isArray(years) && Number.isFinite(years[0]) ? years[0] : null;
  let to = Array.isArray(years) && Number.isFinite(years[1]) ? years[1] : null;
  if (dataYears.length) {
    const lo = Math.min.apply(null, dataYears);
    const hi = Math.max.apply(null, dataYears);
    from = from == null ? lo : Math.min(from, lo);
    to = to == null ? hi : Math.max(to, hi);
  }
  const drawable = from != null && to != null && to >= from;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      const ro = new window.ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [drawable]);

  if (!drawable) return null;
  if (to - from + 1 > MAX_YEARS) from = to - MAX_YEARS + 1;

  const list = [];
  for (let y = from; y <= to; y++) list.push({ year: y, n: counts[y] || 0 });
  const max = Math.max(1, ...list.map((d) => d.n));
  const peak = list.reduce((a, d) => (d.n > a.n ? d : a), list[0]);
  const total = list.reduce((a, d) => a + d.n, 0);

  const slot = width / list.length;
  const barW = Math.max(1, Math.min(24, slot - 2));
  const H = TOP + PLOT_H + BOTTOM;
  const baseY = TOP + PLOT_H;

  // year labels: both ends, plus decade marks with room around them
  const labels = [{ year: from, x: 0, anchor: 'start' }];
  if (to !== from) labels.push({ year: to, x: width, anchor: 'end' });
  list.forEach((d, i) => {
    if (d.year % 10 !== 0 || d.year === from || d.year === to) return;
    const x = i * slot + slot / 2;
    if (x > 44 && x < width - 44) labels.push({ year: d.year, x, anchor: 'middle' });
  });

  const summary =
    'Plays by year, ' + from + ' to ' + to + '. ' + (total ? 'Most in ' + peak.year + ', ' + peak.n + ' plays.' : 'No plays counted.');

  return (
    <div className="vb-ys" ref={wrapRef} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={H} viewBox={'0 0 ' + width + ' ' + H} role="img" aria-label={summary}>
        {list.map((d, i) => {
          const h = d.n ? Math.max(2, (d.n / max) * PLOT_H) : 0;
          const x = i * slot + (slot - barW) / 2;
          return (
            <g
              key={d.year}
              className="vb-ys-slot"
              onMouseEnter={() => setHover({ year: d.year, n: d.n, x: i * slot + slot / 2 })}
            >
              <title>{d.year + ': ' + (d.n === 1 ? '1 play' : d.n + ' plays')}</title>
              <rect className="vb-ys-hit" x={i * slot} y={0} width={slot} height={baseY} rx={3} />
              {h > 0 && <path className="vb-ys-bar" d={colPath(x, baseY - h, barW, h)} />}
            </g>
          );
        })}
        <line className="vb-ys-base" x1={0} x2={width} y1={baseY + 0.5} y2={baseY + 0.5} />
        {peak.n > 0 && (
          <text
            className="vb-ys-peak mono"
            x={Math.min(width - 8, Math.max(8, list.indexOf(peak) * slot + slot / 2))}
            y={baseY - (peak.n / max) * PLOT_H - 4}
            textAnchor="middle"
            aria-hidden="true"
          >
            {peak.n}
          </text>
        )}
        {labels.map((l) => (
          <text key={l.year} className="vb-ys-text" x={l.x} y={H - 3} textAnchor={l.anchor} aria-hidden="true">
            {l.year}
          </text>
        ))}
      </svg>
      {hover && (
        <div className="vb-ys-tip" style={{ left: Math.min(width - 40, Math.max(40, hover.x)) }} aria-hidden="true">
          {hover.year}: {hover.n === 1 ? '1 play' : hover.n + ' plays'}
        </div>
      )}
      <table className="vb-sr-only">
        <caption>Plays by year</caption>
        <thead>
          <tr>
            <th scope="col">Year</th>
            <th scope="col">Plays</th>
          </tr>
        </thead>
        <tbody>
          {list
            .filter((d) => d.n > 0)
            .map((d) => (
              <tr key={d.year}>
                <td>{d.year}</td>
                <td>{d.n}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
